/**
 * Service fee auto-sync ("Apkalpošanas maksa") — v2 (debounced, race-safe)
 * -----------------------------------------------------------------------
 * Adds a 1x service-fee line when the cart subtotal (excluding the fee)
 * is below THRESHOLD_CENTS, normalizes it to qty 1, and removes it once
 * the subtotal reaches the threshold or the cart is empty.
 *
 * Why v2: the previous version reconciled on EVERY cartUpdate immediately
 * and re-rendered the cart synchronously. That raced with the theme's own
 * cart.js (user +/- clicks) and corrupted line indexing ("Parametrs line
 * nav derīgs", disappearing items). v2 debounces reconciliation so it only
 * runs AFTER the shopper stops interacting — the cart DOM stays stable
 * during clicks, and the fee is adjusted once, cleanly.
 *
 * FEE_VARIANT_ID must be the *variant* id of the fee product.
 * Hard guarantee still requires a Shopify Cart Transform Function; this is
 * a client-side convenience layer.
 */
(function () {
  const FEE_VARIANT_ID = 53513472213331;
  const THRESHOLD_CENTS = 2500; // 25.00 EUR
  const DEBUG = true;
  const SYNC_DEBOUNCE_MS = 500; // wait this long after the last cart change

  if (!FEE_VARIANT_ID) {
    console.warn('[service-fee] FEE_VARIANT_ID is not set.');
    return;
  }

  const log = (...args) => DEBUG && console.log('[service-fee]', ...args);

  let isSyncing = false;
  let queued = false;
  let debounceTimer = null;

  const routes = (window.FoxTheme && FoxTheme.routes) || {};
  const CART_URL = routes.cart_url || '/cart';
  const CART_ADD_URL = routes.cart_add_url || '/cart/add';
  const CART_CHANGE_URL = routes.cart_change_url || '/cart/change';

  const jsonHeaders = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };

  function getCartSectionIds() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return Array.from(new Set(sections.filter(Boolean)));
  }

  async function getCart() {
    const res = await fetch(`${CART_URL}.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function cartMutate(url, payload) {
    const body = { ...payload, sections: getCartSectionIds(), sections_url: window.location.pathname };
    const res = await fetch(url, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const error = new Error(`[service-fee] ${url} failed (${res.status}): ${json.description || json.message || res.statusText}`);
      error.response = json;
      error.status = res.status;
      throw error;
    }
    return json;
  }

  function addFee() {
    log('adding fee, variant id', FEE_VARIANT_ID);
    return cartMutate(`${CART_ADD_URL}.js`, { items: [{ id: FEE_VARIANT_ID, quantity: 1 }] });
  }

  function changeLine(lineKey, quantity) {
    log('change line', lineKey, '->', quantity);
    return cartMutate(`${CART_CHANGE_URL}.js`, { id: lineKey, quantity });
  }

  function publishCartUpdate(cart) {
    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.publish) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart, source: 'service-fee' });
    }
    document.documentElement.classList.toggle('cart-has-items', (cart.item_count || 0) > 0);
  }

  async function reconcile(reason) {
    if (isSyncing) {
      queued = true;
      return;
    }
    isSyncing = true;

    try {
      const cart = await getCart();
      const feeLine = cart.items.find((i) => Number(i.variant_id) === FEE_VARIANT_ID);
      const otherItems = cart.items.filter((i) => Number(i.variant_id) !== FEE_VARIANT_ID);
      const merch = otherItems.reduce((s, i) => s + i.final_line_price, 0);
      const cartIsEmpty = otherItems.length === 0;
      log('reconcile(', reason, ') count', cart.item_count, 'merch', merch, 'fee', feeLine && feeLine.quantity);

      let updatedCart = null;

      if (cartIsEmpty && feeLine) {
        updatedCart = await changeLine(feeLine.key, 0);
      } else if (!cartIsEmpty && merch < THRESHOLD_CENTS) {
        if (!feeLine) {
          updatedCart = await addFee();
        } else if (feeLine.quantity !== 1) {
          updatedCart = await changeLine(feeLine.key, 1);
        }
      } else if (merch >= THRESHOLD_CENTS && feeLine) {
        updatedCart = await changeLine(feeLine.key, 0);
      }

      if (updatedCart) {
        if (typeof updatedCart.item_count === 'undefined') {
          const full = await getCart();
          full.sections = updatedCart.sections;
          updatedCart = full;
        }
        publishCartUpdate(updatedCart);
      }
    } catch (err) {
      console.error('[service-fee] reconcile failed:', err);
    } finally {
      isSyncing = false;
      if (queued) {
        queued = false;
        scheduleSync('queued');
      }
    }
  }

  // Debounced entry point: collapses bursts of cart changes (rapid +/- clicks)
  // into a single reconciliation that runs only after the cart has settled.
  function scheduleSync(reason) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => reconcile(reason), SYNC_DEBOUNCE_MS);
  }

  function subscribeToCartUpdates() {
    if (!window.FoxTheme || !FoxTheme.pubsub) return;
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (data) => {
      if (data && data.source === 'service-fee') return; // ignore our own re-render
      scheduleSync('cartUpdate');
    });
  }

  function init() {
    subscribeToCartUpdates();
    scheduleSync('init');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.ServiceFee = { sync: () => reconcile('manual') };
})();
